import { FormEvent, useEffect, useState } from "react";
import { ArrowRight, Mail, MapPin, Phone } from "lucide-react";
import SiteLayout from "@/components/layout/SiteLayout";
import TrustStrip from "@/components/TrustStrip";
import TestimonialsSection, {
  type TestimonialsSectionItem,
} from "@/components/TestimonialsSection";
import MapSection from "@/components/MapSection";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { sendContactEmails } from "@/lib/contactEmails";
import consultationHero from "@/assets/consultation-hero.jpg";

const consultationTestimonials: TestimonialsSectionItem[] = [
  {
    name: "William Geller",
    text: "It is quite easy to recommend Hadassah Homes and Divine Design for their professionalism, coordination, creativity and commitment to stay within the budget they provided on a recent total rehab of a condominium project.",
  },
  {
    name: "Betsy Encarnacion",
    text: "Hadassah Homes completely understood the assignment and brought our vision to life. Beyond being professional and punctual, they were genuinely fun to work with.",
  },
  {
    name: "Jean Ronald Ogando",
    text: "They had only 14 days to remodel the house and worked day and night to finish the project. I was amazed at the results. They truly made the house feel like home.",
  },
];

const interestOptions = [
  "Buying a home",
  "Selling a home",
  "Real estate investing",
  "Dominican Republic property",
  "Property preparation",
  "Interior design",
  "Other",
];

const BookConsultation = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    interest: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    document.title = "Book a Consultation | Hadassah Homes";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        "content",
        "Book a consultation with Hadassah Homes. Get clear, practical guidance for buying, selling, investing, or preparing a property."
      );
    } else {
      const meta = document.createElement("meta");
      meta.name = "description";
      meta.content =
        "Book a consultation with Hadassah Homes. Get clear, practical guidance for buying, selling, investing, or preparing a property.";
      document.head.appendChild(meta);
    }
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = "https://hadassahhomesinc.com/consultation";
  }, []);

  const validate = () => {
    const nextErrors: Record<string, string> = {};

    if (!formData.name.trim()) nextErrors.name = "Please enter your full name.";
    if (!formData.email.trim()) nextErrors.email = "Please enter your email address.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      nextErrors.email = "Please enter a valid email address.";
    }
    if (!formData.interest) nextErrors.interest = "Please select what we can help with.";
    if (!formData.message.trim()) nextErrors.message = "Please share a little about your goals.";

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim() || null,
        interest: formData.interest,
        budget: null,
        preferred_location: null,
        message: formData.message.trim(),
      };

      const id = crypto.randomUUID();
      const { error } = await supabase
        .from("contact_submissions")
        .insert({ ...payload, id });
      if (error) throw error;

      await sendContactEmails(id, { ...payload, source: "Consultation Request" });

      setIsSubmitted(true);
      setFormData({
        name: "",
        email: "",
        phone: "",
        interest: "",
        message: "",
      });
      setErrors({});
    } catch (err) {
      console.error("Consultation submission failed", err);
      setSubmitError(
        "We couldn't send your request. Please try again, or call (781) 632-3936."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SiteLayout navVariant="consultation">
      <section className="relative overflow-hidden bg-[#10271f] pt-24 pb-20 md:pt-28 md:pb-20 lg:pt-36 lg:pb-24">
        <div className="absolute inset-0">
          <img
            src={consultationHero}
            alt="Real estate consultation meeting"
            className="h-full w-full object-cover"
            loading="eager"
            decoding="async"
            width={1920}
            height={1080}
            {...({ fetchpriority: "high" } as any)}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-black/80" />
        </div>

        <div className="relative z-10 mx-auto max-w-wide px-5 md:px-12">
          <div className="grid grid-cols-1 items-stretch gap-8 lg:grid-cols-[minmax(0,0.82fr)_minmax(460px,0.98fr)] lg:items-stretch lg:gap-12">
            <div className="flex flex-col px-1 text-left text-white md:px-0 lg:justify-end">
              <div className="max-w-xl">
              <div>
                <p className="text-[10px] uppercase tracking-[0.28em] text-accent">
                  Book a Consultation
                </p>
                <h1 className="mt-3 mb-5 max-w-lg font-headline text-3xl leading-[1.1] md:text-4xl lg:text-5xl">
                  Let&apos;s Plan Your Next Move
                </h1>
                <p className="max-w-xl text-lg leading-[1.65] text-white/88 md:text-xl">
                  Tell us what you&apos;re working toward, and we&apos;ll help you understand the clearest next step.
                </p>
                <p className="mt-4 max-w-xl text-base leading-[1.7] text-white/76 md:text-lg">
                  Whether you&apos;re buying, selling, investing, or preparing a property, Hadassah Homes gives you practical guidance rooted in strategy, design, and local market insight.
                </p>
              </div>

              <div className="mt-8 border-t border-white/12 pt-8">
                <div className="border border-border bg-white px-5 py-6 md:px-6 md:py-7">
                  <div className="space-y-6">
                    <a
                      href="tel:+17816323936"
                      className="flex items-start gap-3 text-foreground transition-colors duration-200 hover:text-accent"
                    >
                      <Phone className="mt-0.5 h-5 w-5 shrink-0 text-accent" strokeWidth={1.6} />
                      <div>
                        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                          Phone
                        </p>
                        <p className="mt-1 text-[1.15rem] font-medium">(781) 632-3936</p>
                      </div>
                    </a>
                    <a
                      href="mailto:Info@hadassahhomesinc.com"
                      className="flex items-start gap-3 text-foreground transition-colors duration-200 hover:text-accent"
                    >
                      <Mail className="mt-0.5 h-5 w-5 shrink-0 text-accent" strokeWidth={1.6} />
                      <div>
                        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                          Email
                        </p>
                        <p className="mt-1 text-lg">Info@hadassahhomesinc.com</p>
                      </div>
                    </a>
                    <div className="flex items-start gap-3 text-foreground">
                      <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-accent" strokeWidth={1.6} />
                      <div>
                        <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                          Address
                        </p>
                        <p className="mt-1 text-lg">614 Washington St Suite #2, Lynn, MA 01901</p>
                      </div>
                    </div>
                  </div>
                </div>

                <p className="mt-5 text-sm text-white/72 md:text-base">
                  No pressure. No guesswork. Just clear, practical guidance.
                </p>
              </div>
              </div>
            </div>

            <div id="consultation-form" className="lg:self-start">
              <div className="border border-border bg-card p-9 shadow-[0_28px_60px_-28px_rgba(0,0,0,0.4)] md:p-11">
                <h2 className="font-headline text-2xl text-foreground md:text-[2rem]">
                  Request Consultation
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground md:text-base">
                  Share a few details and we&apos;ll help you choose the best next step.
                </p>

                {isSubmitted ? (
                  <div
                    role="status"
                    aria-live="polite"
                    className="mt-8 border border-accent/40 bg-accent/10 px-6 py-8 text-left"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center bg-accent text-accent-foreground">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="square"
                          strokeLinejoin="miter"
                          className="h-5 w-5"
                          aria-hidden="true"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </div>
                      <p className="font-headline text-2xl text-foreground">Thank you</p>
                    </div>
                    <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                      Your request has been received. A member of the Hadassah Homes team will
                      reach out within 24 hours. A confirmation has been sent to your email.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="mt-8 space-y-5">
                    <div>
                      <label className="mb-2 block text-xs uppercase tracking-[0.16em] text-muted-foreground">
                        Full Name
                      </label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="rounded-none border-border bg-background"
                      />
                      {errors.name ? (
                        <p className="mt-1 text-xs text-destructive">{errors.name}</p>
                      ) : null}
                    </div>

                    <div>
                      <label className="mb-2 block text-xs uppercase tracking-[0.16em] text-muted-foreground">
                        Email Address
                      </label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="rounded-none border-border bg-background"
                      />
                      {errors.email ? (
                        <p className="mt-1 text-xs text-destructive">{errors.email}</p>
                      ) : null}
                    </div>

                    <div>
                      <label className="mb-2 block text-xs uppercase tracking-[0.16em] text-muted-foreground">
                        Phone Number
                      </label>
                      <Input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="rounded-none border-border bg-background"
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-xs uppercase tracking-[0.16em] text-muted-foreground">
                        What can we help with?
                      </label>
                      <select
                        value={formData.interest}
                        onChange={(e) => setFormData({ ...formData, interest: e.target.value })}
                        className="flex h-12 w-full rounded-none border border-input bg-background px-4 py-3 text-base text-foreground ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      >
                        <option value="">Select one</option>
                        {interestOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                      {errors.interest ? (
                        <p className="mt-1 text-xs text-destructive">{errors.interest}</p>
                      ) : null}
                    </div>

                    <div>
                      <label className="mb-2 block text-xs uppercase tracking-[0.16em] text-muted-foreground">
                        Message
                      </label>
                      <Textarea
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        placeholder="Tell us a little about your goals..."
                        rows={5}
                        className="rounded-none border-border bg-background"
                      />
                      {errors.message ? (
                        <p className="mt-1 text-xs text-destructive">{errors.message}</p>
                      ) : null}
                    </div>

                    {submitError ? (
                      <p
                        role="alert"
                        aria-live="assertive"
                        className="border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
                      >
                        {submitError}
                      </p>
                    ) : null}

                    <div className="pt-2">
                      <Button
                        variant="gold"
                        size="lg"
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full gap-2 rounded-none text-white shadow-[0_12px_28px_-16px_rgba(191,164,105,0.6)] transition-all duration-300 hover:-translate-y-px"
                      >
                        Request Consultation
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                        We&apos;ll follow up shortly to help you choose the best next step.
                      </p>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="-mt-4 md:-mt-6">
        <TrustStrip />
      </div>

      <div className="pt-4 md:pt-6">
        <TestimonialsSection testimonials={consultationTestimonials} />
      </div>

      <MapSection />
    </SiteLayout>
  );
};

export default BookConsultation;
